// Phase 82a — Map backend tournament documents → UI shape used by AthleteTournamentsPage / Hero
// Keeps the existing component contract so we don't have to refactor Atomic UI components.

import { asset } from "./asset";

const STATUS_FROM_DB = {
  draft: "upcoming",         // hidden from public list anyway
  ready: "upcoming",
  published: "upcoming",
  registration: "upcoming",
  in_progress: "live",
  ongoing: "live",
  completed: "finished",
  archived: "finished",
};

const TYPE_FROM_DB = {
  // Map any backend tournament_type → human label (best-effort; falls back to original).
  ranking: "Ranking",
  professional: "Chuyên nghiệp",
  amateur: "Phong trào",
  mlp: "MLP",
};

function dateRangeLabel(start, end) {
  if (!start) return "Sắp công bố";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const f = (d) =>
    `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  if (!e || isNaN(e.getTime())) return f(s);
  if (s.toDateString() === e.toDateString()) return f(s);
  // Same year/month → "DD - DD.MM.YYYY"
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${String(s.getDate()).padStart(2, "0")} - ${f(e)}`;
  }
  return `${f(s)} - ${f(e)}`;
}

function daysUntil(start) {
  if (!start) return null;
  const s = new Date(start);
  if (isNaN(s.getTime())) return null;
  const now = new Date();
  // v21.x — Lấy mốc là NGÀY GIỜ bắt đầu giải:
  //   • Đã qua giờ bắt đầu   → -1 → "Đang diễn ra".
  //   • Cùng ngày, chưa tới giờ → 0 → "Hôm nay" (tránh Math.ceil làm "Còn 1 ngày").
  //   • Ngày sau             → số ngày lịch còn lại.
  if (now.getTime() >= s.getTime()) return -1;
  const sMid = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const nMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((sMid.getTime() - nMid.getTime()) / (1000 * 60 * 60 * 24));
}

// Phase 83y.53 — Check whether registration window has closed.
// `registration_close_at` is stored as "YYYY-MM-DDTHH:MM" (no TZ from
// datetime-local input). Compare against `new Date()` in local time — same
// reference frame as flatpickr produced the value.
function isRegistrationClosed(closeAtStr) {
  if (!closeAtStr) return false;
  const close = new Date(closeAtStr);
  if (isNaN(close.getTime())) return false;
  return close.getTime() < Date.now();
}

export function mapTournamentToCard(t) {
  if (!t) return null;
  const uiStatus = STATUS_FROM_DB[t.status] || "upcoming";
  const province = t.venue?.province || "";
  const startDate = t.start_date || t.registration_open_at;
  const regClosed = isRegistrationClosed(t.registration_close_at);
  return {
    id: t.id,
    slug: t.slug,
    title: (t.name || "").toUpperCase(),
    subtitle: t.organizer_name || "",
    date: dateRangeLabel(startDate, t.end_date),
    location: (() => {
      const vn = t.venue?.name || "";
      const dist = t.venue?.district || "";
      if (vn) return dist ? `${vn}, ${dist}` : vn;
      return province;
    })(),
    province,
    // v21.x — Khu vực giải đấu (tên tỉnh đầy đủ) từ BE → badge trên card.
    regionProvinces: Array.isArray(t.region_provinces) ? t.region_provinces.filter(Boolean) : [],
    type: TYPE_FROM_DB[t.tournament_type] || (t.tournament_type ? String(t.tournament_type) : "Phong trào"),
    status: uiStatus,
    // Phase 83y.53 — Surface registration-window state so card UI shows
    // "Hết hạn đăng ký" badge + disables the register CTA. Backend status
    // remains "upcoming" while reg window is closed.
    registrationClosed: regClosed,
    registration_close_at: t.registration_close_at || null,
    daysLeft: uiStatus === "upcoming" ? daysUntil(startDate) : null,
    banner: t.banner_url || asset("/home-assets/tournaments/tour-1.webp"),
    logo: t.logo_url || asset("/home-assets/tournament-logos/logo-superstars.webp"),
    quickStats: {
      events: t.events_count || 0,
      // Phase 85.A.107 — `approved_count` giờ là số VĐV thật (đôi×2 + đơn×1).
      players: t.approved_count ?? t.registered_count ?? 0,
      courts: t.courts_count || 0,
      livestreams: t.livestreams_count || 0,
    },
    registration: {
      // Phase 85.A.107 — `current` = số VĐV, `max` = max_athletes (đôi×2 + đơn×1).
      current: t.approved_count ?? t.registered_count ?? 0,
      pending: t.pending_count || 0,
      max: t.max_athletes ?? t.max_teams ?? 0,
    },
    bookmark: false,
    entry_fee_vnd: t.entry_fee_vnd || 0,
    // Phase 83z.12 — Surface raw total_prize_pool so home slider can render
    // the eye-catching gold prize-pool badge. Falls back to 0 → FE hides.
    totalPrizePool: Number(t.total_prize_pool || 0),
    registrationStatus: regClosed ? "closed" : "open",
    // Phase 85.A.212 — Demo flag → FE render badge "DEMO" để VĐV biết đây là
    // giải dùng dữ liệu giả lập (BTC bấm Công bố cho phép public truy cập).
    isDemo: !!t.is_demo,
    // Phase 85.A.223 — Khoá đăng ký (chỉ xem). FE ẩn nút đăng ký/thanh toán
    // và hiện nhãn "Chỉ xem". Backend là lớp chặn thật (anti-tamper).
    registrationLocked: !!t.registration_locked,
    registration_locked: !!t.registration_locked,
    // v20.9-WW — Shallow raw: only scalar fields actually consumed by
    // downstream components (useHomeData sort, AthleteTournamentsPage filter,
    // Step3Teammates tournamentId fallback). Avoids carrying the entire
    // deeply-nested backend payload through React/Framer Motion, which was
    // the leading candidate for Sentry REACT-NATIVE-Y RangeError stack overflow.
    raw: {
      id: t.id,
      start_date: t.start_date,
      registration_open_at: t.registration_open_at,
    },
  };
}

export function mapTournamentsList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapTournamentToCard).filter(Boolean);
}

// ─── Phase 82b — Tournament detail mapping ───
// Phase 83y.7 — `disciplineLabel`/`genderLabel` derived from the shared
// `getEventFormatLabel(ev)` helper so the public list, registration wizard
// and admin tag all match exactly. Keep the discipline/gender literal maps
// as a fallback for display when the event doesn't fit any standard format.
import { getEventFormatId, getEventFormatLabel, getEventFormatMeta } from "./eventFormat";

const EVENT_DISCIPLINE_LABEL = {
  singles: "Đơn",
  doubles: "Đôi",
  mixed: "Đôi hỗn hợp",
  mixed_doubles: "Đôi hỗn hợp",
};
const EVENT_GENDER_LABEL = {
  male: "Nam",
  female: "Nữ",
  mixed: "Hỗn hợp",
  mixed_pair: "Nam nữ",
};

function mapEventToTab(ev) {
  if (!ev) return null;
  const ratingLabel =
    ev.age_min && ev.age_max
      ? `${ev.age_min}-${ev.age_max}`
      : ev.age_min || ev.age_max || "Open";
  const max = ev.max_teams || 0;
  const rawReg = ev.registered_count || 0;
  // Phase 88.E.53 — Với minigame `random_partner`, mỗi VĐV đăng ký đơn lẻ
  // (1 registration = 1 người). Hiển thị theo "đội" phải chia 2 (floor) vì
  // BTC sẽ ghép cặp ngẫu nhiên sau. VD: 17 VĐV → 8 đội, 22 VĐV → 11 đội.
  // "Slot đầy" cũng phải so theo cặp: cần `max*2` VĐV mới full.
  const isRandom = !!ev.random_partner;
  const reg = isRandom ? Math.floor(rawReg / 2) : rawReg;
  let status = "open";
  if (max && (isRandom ? rawReg >= max * 2 : rawReg >= max)) status = "full";
  return {
    id: ev.id,
    name: ev.name,
    // Phase 83y.7 — Keep raw discipline/gender (for filters etc) but expose
    // a single `formatLabel` derived from the shared helper so card UI shows
    // "Đôi nam nữ" instead of inconsistent ad-hoc combinations.
    discipline: ev.discipline || "doubles",
    disciplineLabel: EVENT_DISCIPLINE_LABEL[ev.discipline] || "Đôi",
    gender: ev.gender || "male",
    genderLabel: EVENT_GENDER_LABEL[ev.gender] || "Nam",
    formatId: getEventFormatId(ev),
    formatLabel: getEventFormatLabel(ev),
    formatMeta: getEventFormatMeta(ev),
    event_type: ev.event_type,
    rating: ratingLabel,
    rating_min: ev.rating_min,
    rating_max: ev.rating_max,
    tolerance: ev.tolerance,  // Phase 83y.17 — surface dung sai
    // Phase 83y.64 — preserve `prizes` so PrizeStructureCard (hero card on
    // athlete tournament detail) renders the BTC's announced prizes.
    // Previously dropped → user saw "BTC chưa công bố cơ cấu giải thưởng"
    // even when data was present.
    prizes: Array.isArray(ev.prizes) ? ev.prizes : [],
    // Phase 84.M.77 — preserve `bronze_mode` for PrizeStructureCard to
    // correctly multiply rank=3 cash ×2 when 'joint' (2 đội đồng hạng).
    bronze_mode: ev.bronze_mode || (Array.isArray(ev.prizes) && ev.prizes.some(p => p.rank === 3) ? "joint" : null),
    registered: reg,
    max,
    // Phase 83y.3 — Use event-level entry_fee_vnd set by admin in the
    // Tournament wizard (Step 2). Previously hardcoded to 0, so the
    // payment screen always showed "0đ / Miễn phí" even when admin
    // configured a real fee.
    fee: ev.entry_fee_vnd || 0,
    entry_fee_vnd: ev.entry_fee_vnd || 0,
    // v16.85 — Format label hệ thức thi đấu lấy từ BE (khớp với section 4
    // Điều lệ). Fallback về "Vòng bảng - Loại trực tiếp" (Karinox chuẩn) khi
    // BE chưa emit field mới. TRƯỚC ĐÓ: hardcoded "Vòng bảng" → mâu thuẫn
    // với điều lệ "Loại trực tiếp".
    format: ev.competition_format_label || "Vòng bảng - Loại trực tiếp",
    competitionFormat: ev.competition_format || "round_robin_ko",
    status,
    // Phase 90.B.minigame.random-partner.expose-flag — Carry random_partner
    // flag through to wizard so `state.event?.random_partner` works trong
    // skip-step-teammates logic + Confirm-step notice banner.
    random_partner: !!ev.random_partner,
    // Phase 90.B.minigame.random-partner.balanced — Carry 4 rating limits
    // theo giới tính để Step4Confirm hiển thị "Min < điểm bạn < Max" theo
    // gender VĐV.
    rating_min_male: ev.rating_min_male ?? null,
    rating_max_male: ev.rating_max_male ?? null,
    rating_min_female: ev.rating_min_female ?? null,
    rating_max_female: ev.rating_max_female ?? null,
    // v16.61.7 (Phase 90.F) — Cờ khoá đăng ký cấp nội dung. EventCard sẽ
    // xám mờ card + chặn click + hiện toast khi VĐV cố bấm.
    registration_locked: !!ev.registration_locked,
    // v21.54 — Social Challenge: tỷ lệ giá vé + nhãn livestream + quỹ động.
    ticket_breakdown_enabled: !!ev.ticket_breakdown_enabled,
    social_fee_vnd: ev.social_fee_vnd ?? 0,
    prize_fund_vnd: ev.prize_fund_vnd ?? 0,
    show_livestream_label: !!ev.show_livestream_label,
    estimated_prize_fund_vnd: ev.estimated_prize_fund_vnd ?? 0,
    registered_count: ev.registered_count ?? reg ?? 0,
  };
}

export function mapTournamentDetail(t) {
  if (!t) return null;
  const card = mapTournamentToCard(t);
  return {
    ...card,
    organizer: t.organizer_name || "PickLeague",
    bannerLarge: t.banner_url || asset("/home-assets/tournaments/tour-1.webp"),
    description: t.description || "",
    quickStats: {
      events: (t.events || []).length,
      players: (t.events || []).reduce(
        (s, ev) => s + (ev.registered_count || 0),
        0
      ),
      courts: 0,
      livestreams: 0,
      matches: 0,
    },
    events: (t.events || []).map(mapEventToTab).filter(Boolean),
    has_qr: !!t.has_qr,
    // v16 — Zalo contact (link nhóm/SĐT) → dùng cho ZaloFloatButton.
    zalo_contact: t.zalo_contact || null,
    btc_roster: Array.isArray(t.btc_roster) ? t.btc_roster : [],
    // v20.9-WW — Shallow raw for detail: scalar fields consumed by
    // RulesTab, EventsTab (RulesInline), and registration components.
    // Venue is shallow-copied (3 scalars only). Full backend payload is
    // NOT retained — prevents deep-nesting stack overflow (Sentry REACT-NATIVE-Y).
    raw: {
      id: t.id,
      start_date: t.start_date,
      end_date: t.end_date,
      start_at: t.start_at,
      end_at: t.end_at,
      registration_open_at: t.registration_open_at,
      registration_close_at: t.registration_close_at,
      registration_open: t.registration_open,
      registration_close: t.registration_close,
      description: t.description,
      entry_fee_vnd: t.entry_fee_vnd,
      has_qr: t.has_qr,
      organizer_name: t.organizer_name,
      venue: t.venue ? { name: t.venue.name, address: t.venue.address, province: t.venue.province, district: t.venue.district } : null,
    },
  };
}
